import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { useDateTimeData } from '../../../shared/hooks/useDateTimeData';
import { Service } from '../../../shared/hooks/useServiceSelection';
import { useResponsive } from '../../../shared/hooks/useResponsive';

interface ImmersiveDateTimeDisplayProps {
  selectedServices: Service[];
  selectedTimeZone: string;
  selectedLocation: any;
  selectedDate: Date | null;
  setSelectedDate: (date: Date) => void;
  selectedTime: string | null;
  setSelectedTime: (time: string) => void;
  onNext: () => void;
  onPrev: () => void;
}

const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function generateDates(count = 42): Date[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const DATES = generateDates();

const TIME_GROUPS = [
  { key: 'morning' as const, label: 'Morning' },
  { key: 'afternoon' as const, label: 'Afternoon' },
  { key: 'evening' as const, label: 'Evening' },
];

const ImmersiveDateTimeDisplay: React.FC<ImmersiveDateTimeDisplayProps> = ({
  selectedServices,
  selectedTimeZone,
  selectedLocation,
  selectedDate: externalDate,
  setSelectedDate: externalSetDate,
  setSelectedTime: externalSetTime,
  onNext,
}) => {
  const { isMobile } = useResponsive();

  const {
    selectedDate,
    setSelectedDate,
    setSelectedTime,
    timeGroups,
    fetching,
    hasAvailableSlots,
  } = useDateTimeData({
    selectedServices,
    selectedTimeZone,
    selectedLocation,
    initialDate: externalDate,
  });

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    externalSetDate(date);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    externalSetTime(time);
    setTimeout(() => onNext(), 280);
  };

  const formattedSelectedDate = selectedDate
    ? `${SHORT_DAYS[selectedDate.getDay()]}, ${selectedDate.getDate()} ${FULL_MONTHS[selectedDate.getMonth()]}`
    : '';

  if (isMobile) {
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', background: '#0a0a0a', overflow: 'hidden' }}>
        <style>{`#lp-date-m::-webkit-scrollbar { display: none; } #lp-time-m::-webkit-scrollbar { display: none; }`}</style>

        {/* ── Dates — top ~45% ── */}
        <div id="lp-date-m" style={{ flex: '0 0 45vh', overflowY: 'auto', scrollbarWidth: 'none', padding: '24px 20px 8px' }}>
          <p style={{ color: 'rgba(255,255,255,0.22)', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 12 }}>
            Select a date
          </p>
          {DATES.map((date, i) => {
            const isSel = selectedDate ? isSameDay(date, selectedDate) : false;
            const isToday = isSameDay(date, new Date());
            return (
              <motion.div
                key={i}
                onClick={() => handleDateSelect(date)}
                animate={{ background: isSel ? 'rgba(255,255,255,0.09)' : 'transparent' }}
                transition={{ duration: 0.18 }}
                style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${isSel ? 'rgba(255,255,255,0.14)' : 'transparent'}`, marginBottom: 2 }}
              >
                <span style={{ width: 30, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: isSel ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
                  {SHORT_DAYS[date.getDay()]}
                </span>
                <span style={{ flex: 1, fontSize: 26, fontWeight: 600, lineHeight: 1, color: isSel ? '#fff' : 'rgba(255,255,255,0.45)', letterSpacing: '-0.02em' }}>
                  {date.getDate()}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <span style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: isSel ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.18)' }}>
                    {FULL_MONTHS[date.getMonth()].slice(0, 3)}
                  </span>
                  {isToday && <span style={{ fontSize: 8, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>today</span>}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '0 20px', flexShrink: 0 }} />

        {/* ── Times — bottom ── */}
        <div id="lp-time-m" style={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'none', padding: '16px 20px 24px' }}>
          <AnimatePresence mode="wait">
            {!selectedDate ? (
              <motion.p key="prompt" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ color: 'rgba(255,255,255,0.15)', fontSize: 13, letterSpacing: '0.06em' }}>
                Pick a date to see times
              </motion.p>
            ) : fetching ? (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 8 }}>
                {[0, 1, 2].map(i => (
                  <motion.div key={i} animate={{ opacity: [0.15, 0.5, 0.15] }}
                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                    style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.4)' }} />
                ))}
              </motion.div>
            ) : !hasAvailableSlots ? (
              <motion.div key="empty" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
                  {formattedSelectedDate}
                </p>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>No slots available</p>
                <p style={{ color: 'rgba(255,255,255,0.15)', fontSize: 12, marginTop: 6 }}>Try a different date</p>
              </motion.div>
            ) : (
              <motion.div key="slots" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {formattedSelectedDate}
                </p>
                {TIME_GROUPS.map(({ key, label }) => {
                  const slots = timeGroups?.[key] ?? [];
                  if (slots.length === 0) return null;
                  return (
                    <div key={key}>
                      <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 10 }}>
                        {label}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {slots.map((time: string) => (
                          <motion.button key={time} onClick={() => handleTimeSelect(time)} whileTap={{ scale: 0.96 }}
                            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 100, padding: '9px 18px', color: 'rgba(255,255,255,0.82)', fontSize: 13, letterSpacing: '0.06em', cursor: 'pointer' }}>
                            {time}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100vh', overflow: 'hidden', display: 'flex', background: '#0a0a0a' }}>
      <style>{`#lp-date-list::-webkit-scrollbar { display: none; }`}</style>

      {/* ── LEFT: Date list ────────────────────────────────── */}
      <div
        id="lp-date-list"
        style={{
          width: '50%',
          height: '100%',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          padding: '72px 56px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <p style={{
          color: 'rgba(255,255,255,0.22)',
          fontSize: 10,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          marginBottom: 32,
        }}>
          Select a date
        </p>

        {DATES.map((date, i) => {
          const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
          const isToday = isSameDay(date, new Date());

          return (
            <motion.div
              key={i}
              onClick={() => handleDateSelect(date)}
              whileHover={{ background: 'rgba(255,255,255,0.06)' }}
              whileTap={{ scale: 0.99 }}
              animate={{
                background: isSelected ? 'rgba(255,255,255,0.09)' : 'transparent',
              }}
              transition={{ duration: 0.18 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '14px 20px',
                borderRadius: 12,
                cursor: 'pointer',
                border: `1px solid ${isSelected ? 'rgba(255,255,255,0.14)' : 'transparent'}`,
              }}
            >
              {/* Day abbreviation */}
              <span style={{
                width: 36,
                fontSize: 10,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: isSelected ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.2)',
                flexShrink: 0,
              }}>
                {SHORT_DAYS[date.getDay()]}
              </span>

              {/* Day number — big */}
              <span style={{
                flex: 1,
                fontSize: 42,
                fontWeight: 600,
                lineHeight: 1,
                color: isSelected ? '#ffffff' : 'rgba(255,255,255,0.45)',
                letterSpacing: '-0.02em',
              }}>
                {date.getDate()}
              </span>

              {/* Month + today dot */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <span style={{
                  fontSize: 11,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: isSelected ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.18)',
                }}>
                  {FULL_MONTHS[date.getMonth()].slice(0, 3)}
                </span>
                {isToday && (
                  <span style={{
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.35)',
                  }}>
                    today
                  </span>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* ── Divider ─────────────────────────────────────────── */}
      <div style={{ width: 1, flexShrink: 0, background: 'rgba(255,255,255,0.07)', alignSelf: 'stretch', margin: '40px 0' }} />

      {/* ── RIGHT: Time slots ───────────────────────────────── */}
      <div style={{
        width: '50%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '72px 56px',
        overflowY: 'auto',
        scrollbarWidth: 'none',
      }}>
        <AnimatePresence mode="wait">
          {!selectedDate ? (
            <motion.p
              key="prompt"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                color: 'rgba(255,255,255,0.15)',
                fontSize: 14,
                letterSpacing: '0.08em',
              }}
            >
              ← Pick a date to see available times
            </motion.p>
          ) : fetching ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ display: 'flex', flexDirection: 'column', gap: 32, width: '100%' }}
            >
              {/* Selected date heading */}
              <p style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 13,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}>
                {formattedSelectedDate}
              </p>
              {/* Loading dots */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {[0, 1, 2].map(i => (
                  <motion.div
                    key={i}
                    animate={{ opacity: [0.15, 0.5, 0.15] }}
                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                    style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.4)' }}
                  />
                ))}
              </div>
            </motion.div>
          ) : !hasAvailableSlots ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <p style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 13,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                marginBottom: 20,
              }}>
                {formattedSelectedDate}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14, letterSpacing: '0.04em' }}>
                No slots available
              </p>
              <p style={{ color: 'rgba(255,255,255,0.15)', fontSize: 12, marginTop: 8, letterSpacing: '0.04em' }}>
                Try a different date
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="slots"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 36 }}
            >
              {/* Selected date heading */}
              <p style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 13,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}>
                {formattedSelectedDate}
              </p>

              {TIME_GROUPS.map(({ key, label }) => {
                const slots = timeGroups?.[key] ?? [];
                if (slots.length === 0) return null;
                return (
                  <div key={key}>
                    <p style={{
                      color: 'rgba(255,255,255,0.2)',
                      fontSize: 10,
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      marginBottom: 14,
                    }}>
                      {label}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {slots.map((time: string) => (
                        <motion.button
                          key={time}
                          onClick={() => handleTimeSelect(time)}
                          whileHover={{ background: 'rgba(255,255,255,0.15)', scale: 1.04 }}
                          whileTap={{ scale: 0.96 }}
                          style={{
                            background: 'rgba(255,255,255,0.07)',
                            backdropFilter: 'blur(14px)',
                            WebkitBackdropFilter: 'blur(14px)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: 100,
                            padding: '10px 22px',
                            color: 'rgba(255,255,255,0.82)',
                            fontSize: 14,
                            letterSpacing: '0.08em',
                            cursor: 'pointer',
                          }}
                        >
                          {time}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ImmersiveDateTimeDisplay;
